import React, { useCallback, useLayoutEffect, useRef } from 'react'
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import colors from '../../theme/colors'
import { iconArray } from '../../assets/icons/icons'

am4core.useTheme(am4themes_animated);

const styles = {
    rowFlex: () => ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
    }),
    chartDiv: (orientation, hasLegend) => ({
        width: "100%",
        height: (orientation === 'vertical') ?
            hasLegend ?
                450 :
                475 :
            330,
    }),
    iconText: (color) => ({
        color: color,
        marginTop: '12px',
        textAlign: 'center'
    }),
    center: () => ({
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center'
    })
}


function AmBarChart({
    data,
    seriesNames,
    amchartId,
    orientation,
    hasLegend,
    isSentiment,
    isDateAxis,
    styleProps,
    amchartTitle,
    renderedCharts
}) {

    const chart = useRef(null)

    const setBulletText = useCallback(({ bullet, orientation, sItem, isSentiment }) => {
        let valueAlpha
        let differential, positionValue
        switch (orientation) {
            case 'horizontal':
                valueAlpha = 'valueX'
                differential = 'dx'
                positionValue = 15
                break
            case 'vertical':
                valueAlpha = 'valueY'
                differential = 'dy'
                positionValue = -15
                break
        }
        bullet.interactionsEnabled = false;
        bullet.label.text = `{${valueAlpha}}`
        bullet.label[differential] = positionValue
        bullet.label.fontSize = 18
        bullet.label.truncate = false

        // add red / green / grey color for sentiments bar chart
        if (styleProps.colorizeLabels) {
            bullet.label.adapter.add("fill", (fill, target) => {
                if (!target.dataItem) {
                    return fill;
                }
                if (isSentiment) {
                    return colors[sItem.color]
                }
                return '#000'
            });
        }
        // add green border
        if (styleProps.positiveLabelBorder) {
            if (sItem.key.includes('positive')) {
                bullet.label.background.stroke = am4core.color(colors[sItem.color])
                bullet.label.background.strokeOpacity = 0.5;
            }
        }
        if (styleProps.boldText) {
            bullet.label.fontWeight = '600'
        }

        if (orientation === 'horizontal') {
            bullet.label.fontWeight = styleProps.labelFontWeight ? 'bolder' : 'normal'
            bullet.label.fill = '#000000'
            bullet.label.fontSize = 14
            bullet.label.horizontalCenter = 'left'
            bullet.label.dx = 8;
        } else bullet.label.fill = '#404040'
        return bullet
    })

    const setSeries = useCallback(({ series, sItem }) => {
        let valueShow, categoryAlpha, valueAlpha, alphaAxis
        switch (orientation) {
            case 'horizontal':
                valueShow = 'valueXShow'
                categoryAlpha = 'categoryY'
                valueAlpha = 'valueX'
                alphaAxis = 'yAxis'
                series.columns.template.height = am4core.percent(45);
                break;
            case 'vertical':
                valueShow = 'valueYShow'
                categoryAlpha = isDateAxis ? 'dateX' : 'categoryX'
                valueAlpha = 'valueY'
                alphaAxis = 'xAxis'
                series.columns.template.width = am4core.percent(55);
                break;
        }
        series.name = sItem.title || sItem.key;
        // series.tooltip.pointerOrientation = "vertical";

        series.dataFields[categoryAlpha] = "x";
        series.dataFields[valueAlpha] = sItem.key;
        // series[alphaAxis] = categoryAxis;

        let textToShow = `{${valueAlpha}.formatNumber('#.')}`;

        series.tooltipText = "{name}: " + textToShow;

        // set Columns color
        series.columns.template.fill = colors[sItem.color]
        series.columns.template.stroke = colors[sItem.color]

        return series
    })

    const icon = () => {
        const outletsIcon = iconArray(styleProps.iconColor)[7]
        const globeIcon = iconArray(styleProps.iconColor)[5]
        const languageIcon = iconArray(styleProps.iconColor)[6]

        const boldStyle = styleProps.boldHeaders ? { fontWeight: 'bolder' } : {}

        if (orientation !== 'vertical') {
            return (
                <div style={styles.rowFlex()}>
                    <div style={{ transform: 'scale(1.9)', height: 96, ...styles.center() }}>{
                        amchartTitle.includes('Local') ? outletsIcon :
                            amchartTitle.includes('International') || amchartTitle.includes('Countries') ? globeIcon :
                                languageIcon
                    }</div>
                    <h2 style={{ ...styles.iconText(styleProps.iconColor), ...boldStyle }}>{amchartTitle}</h2>
                </div>
            )
        }
        return (<></>)
    }

    useLayoutEffect(() => {
        // renderedCharts.current[amchartId] = false

        let x = am4core.create(`chartdiv-bar-${amchartId}`, am4charts.XYChart);

        x.padding(30, 30, 10, 30);

        let categoryAxis
        if (orientation === "vertical") {
            if (isDateAxis) {
                categoryAxis = x.xAxes.push(new am4charts.DateAxis());
                categoryAxis.baseInterval = {
                    timeUnit: "day",
                    count: 1,
                };
                categoryAxis.dateFormats.setKey("day", "d-MMM")
            } else categoryAxis = x.xAxes.push(new am4charts.CategoryAxis());
            categoryAxis.renderer.labels.template.fontSize = 10

        } else {
            categoryAxis = x.yAxes.push(new am4charts.CategoryAxis());
            categoryAxis.renderer.line.strokeOpacity = 0.2;
            categoryAxis.renderer.line.strokeWidth = 1;
            if (styleProps.labelColor)
                categoryAxis.renderer.labels.template.fill = am4core.color(styleProps.labelColor);
            categoryAxis.renderer.labels.template.fontSize = 13
        }
        categoryAxis.dataFields.category = "x";
        categoryAxis.renderer.grid.template.location = 0;
        categoryAxis.renderer.grid.template.disabled = true
        categoryAxis.renderer.minGridDistance = 10;
        categoryAxis.renderer.cellStartLocation = 0.15
        categoryAxis.renderer.cellEndLocation = 0.85

        categoryAxis.renderer.labels.template.wrap = orientation === 'vertical'

        categoryAxis.events.on("sizechanged", function (ev) {
            let axis = ev.target;
            axis.renderer.labels.template.maxWidth = axis.pixelWidth / (axis.endIndex - axis.startIndex);
        });

        if (isSentiment) {
            categoryAxis.renderer.labels.template.fill = am4core.color('#5c5c5c')
        } else {
            categoryAxis.renderer.labels.template.fontWeight = styleProps.labelFontWeight ? styleProps.labelFontWeight : '400'
        }

        let valueAxis;
        if (orientation === "vertical") {
            valueAxis = x.yAxes.push(new am4charts.ValueAxis());
            valueAxis.renderer.grid.template.strokeOpacity = .4
            valueAxis.renderer.grid.template.stroke = am4core.color(styleProps.yGridColor)
            if (!styleProps.showYAxis) {
                valueAxis.renderer.labels.template.disabled = true
            }
        } else {
            valueAxis = x.xAxes.push(new am4charts.ValueAxis());
            valueAxis.renderer.grid.template.disabled = true
            valueAxis.renderer.labels.template.disabled = true
        }
        valueAxis.calculateTotals = true;
        valueAxis.renderer.minWidth = 50;
        valueAxis.renderer.labels.template.fontSize = 16
        valueAxis.renderer.labels.template.fill = am4core.color('#5c5c5c')

        // set series and bullets for the chart
        seriesNames.map(sItem => {
            let series = x.series.push(new am4charts.ColumnSeries());

            let bullet = series.bullets.push(new am4charts.LabelBullet());

            setSeries({ series: series, orientation, sItem, categoryAxis })
            setBulletText({ bullet, orientation, sItem, isSentiment })
        })

        x.cursor = new am4charts.XYCursor();
        x.maskBullets = false
        x.data = orientation === 'vertical' ? data : data.reverse()
        //time.formatTimeStrings(resultSet.chartPivot()) : time.formatTimeStrings(resultSet.chartPivot().reverse())
        // And, for a good measure, let's add a legend
        if (hasLegend) {
            x.legend = new am4charts.Legend();
            x.legend.labels.template.fontWeight = 'bold'

        }

        x.logo.disabled = true

        x.events.on("ready", () => {
            renderedCharts.current[amchartId] = true
            // console.log('BAR RENDERED', renderedCharts);
        })

        chart.current = x;

        return () => {
            x.dispose();
        };
    }, []);

    return (
        <div style={{ ...styles.rowFlex() }}>
            {icon()}
            <div id={`chartdiv-bar-${amchartId}`} style={styles.chartDiv(orientation, hasLegend)} />
        </div>
    );
}

AmBarChart.defaultProps = {
    styleProps: {
        showYAxis: true,
        boldText: true,
        colorizeLabels: false,
        positiveLabelBorder: false,
        barColor: colors.NAVY,
        barStroke: colors.NAVY,
        iconColor: colors.NAVY,
    }
}

export default AmBarChart;